import type { KLineData } from 'klinecharts'
import { computePivotPoints, type PivotLevels } from './pivotPointsIndicator'
import { buildDayExtremes } from './previousDayHighLow'
import { computeVwap } from './vwapIndicator'
import { emaSeries, rollingMean, trueRangeSeries, wilderSeries, type Num } from './seriesMath'
import { DEFAULT_SESSION_TZ } from './sessionDay'
import { isNum } from './overlayUtils'

/**
 * Position sizing readout — pure math for the POSITION_SIZE indicator.
 *
 * Core semantics (spec: docs/superpowers/specs/2026-08-24-position-sizing-indicator-design.md):
 * a reference level is relevant only when it sits on the ADVERSE side of price
 * for the chosen direction — LONG needs its stop below, SHORT above — and
 * multi-level sources bind the NEAREST adverse level. From there,
 * shares = floor(risk$ / |price - reference|).
 *
 * The dash states are deliberately distinct — they mean different things to a
 * trader:
 *   'no-data'    — the source has no value yet (no lines drawn, MA warm-up,
 *                  first session in view)
 *   'wrong-side' — levels exist, but none on the adverse side
 *   'too-close'  — a level is bound but sits inside the epsilon guard, where
 *                  the share count would explode as price crosses it
 */

export type SizingDirection = 'long' | 'short'

export type SizingSource = 'manual' | 'pivot' | 'pdh' | 'pdl' | 'sma' | 'ema' | 'vwap' | 'atr'

/** A user-drawn horizontal level, snapshotted by ChartManager into extendData. */
export interface ManualLevel {
  price: number
  /** Ray anchor time; null for a full horizontal line (active at all times). */
  timestamp: number | null
}

export interface ReferenceCandidate {
  price: number
  label: string
}

export type ReferenceSelection =
  | { state: 'level'; price: number; label: string }
  | { state: 'wrong-side' }
  | { state: 'no-data' }

export type SizingReadout =
  | {
      state: 'ok'
      label: string
      refPrice: number
      distance: number
      shares: number
      realized: number
      /** Last close the size was computed against. */
      price: number
      /** Capital deployed: price * shares. Distinct from `realized` risk. */
      positionValue: number
      /** True when maxPositionDollars, not the risk budget, set the size. */
      capped: boolean
    }
  | { state: 'no-data' }
  | { state: 'wrong-side' }
  | { state: 'too-close' }

export interface SizingInputs {
  direction: SizingDirection
  riskDollars: number
  /**
   * Hard ceiling on capital deployed (price * shares). 0 or undefined means
   * no ceiling. When it binds it OVERRIDES the risk-derived size, so the
   * trader never exceeds what the account should carry on one position.
   */
  maxPositionDollars?: number
  source: SizingSource
  maPeriod: number
  bars: KLineData[]
  manualLevels: ManualLevel[]
  timezone?: string
  /** Minimum price increment at the chart's precision (10^-precision). */
  tick: number
}

/**
 * The adverse-side relevance rule. A level exactly AT price is not adverse for
 * either direction (there is no risk distance to size against).
 */
export function selectReference(
  direction: SizingDirection,
  price: number,
  candidates: ReferenceCandidate[],
): ReferenceSelection {
  const valid = candidates.filter((c) => isNum(c.price))
  if (valid.length === 0) return { state: 'no-data' }
  const adverse = valid.filter((c) => (direction === 'long' ? c.price < price : c.price > price))
  if (adverse.length === 0) return { state: 'wrong-side' }
  let best = adverse[0]
  for (const c of adverse) {
    if (Math.abs(price - c.price) < Math.abs(price - best.price)) best = c
  }
  return { state: 'level', price: best.price, label: best.label }
}

/**
 * Manual-mode candidates: full lines always count; a ray counts once its
 * anchor time has been reached (rays extend rightward — see
 * horizontalRayOverlay.ts), so one anchored in the future is not yet a level.
 */
export function manualCandidates(
  levels: ManualLevel[],
  lastTimestamp: number,
): ReferenceCandidate[] {
  const out: ReferenceCandidate[] = []
  for (const l of levels) {
    if (!isNum(l.price)) continue
    if (l.timestamp === null) out.push({ price: l.price, label: 'Line' })
    else if (l.timestamp <= lastTimestamp) out.push({ price: l.price, label: 'Ray' })
  }
  return out
}

/** The overlay names that count as manual sizing levels. */
export const MANUAL_LEVEL_OVERLAY_NAMES: ReadonlySet<string> = new Set([
  'horizontalStraightLine',
  'horizontalRayLine',
])

/** The slice of a klinecharts overlay instance the snapshot needs. */
export interface OverlayLevelInstance {
  name: string
  visible?: boolean
  points?: Array<{ timestamp?: number; value?: number }>
}

/**
 * Live overlay instances → manual levels for extendData. Lines are
 * always-active (timestamp null); rays carry their anchor time so the
 * candidate builder can ignore ones anchored in the future. Hidden overlays
 * do not size a position they cannot be seen at.
 */
export function manualLevelsFromOverlays(instances: OverlayLevelInstance[]): ManualLevel[] {
  const out: ManualLevel[] = []
  for (const o of instances) {
    if (!MANUAL_LEVEL_OVERLAY_NAMES.has(o.name) || o.visible === false) continue
    const point = o.points?.[0]
    if (!point || !isNum(point.value)) continue
    const timestamp =
      o.name === 'horizontalRayLine' && isNum(point.timestamp) ? point.timestamp : null
    out.push({ price: point.value, timestamp })
  }
  return out
}

const PIVOT_LABELS: Array<[keyof PivotLevels, string]> = [
  ['p', 'P'],
  ['r1', 'R1'],
  ['r2', 'R2'],
  ['r3', 'R3'],
  ['s1', 'S1'],
  ['s2', 'S2'],
  ['s3', 'S3'],
]

function pivotCandidates(bars: KLineData[], tz: string): ReferenceCandidate[] {
  const perBar = computePivotPoints(bars, tz)
  const levels = perBar[perBar.length - 1]
  if (!levels) return []
  const out: ReferenceCandidate[] = []
  for (const [key, label] of PIVOT_LABELS) {
    const price = levels[key]
    if (isNum(price)) out.push({ price, label })
  }
  return out
}

function prevDayCandidates(
  bars: KLineData[],
  tz: string,
  mode: 'high' | 'low',
): ReferenceCandidate[] {
  const extremes = buildDayExtremes(bars, tz, mode)
  // The level for the LAST day in view is the PREVIOUS day's extreme.
  if (extremes.length < 2) return []
  const prev = extremes[extremes.length - 2].value
  if (!isNum(prev)) return []
  return [{ price: prev, label: mode === 'high' ? 'PDH' : 'PDL' }]
}

/** Arithmetic mean of the last `period` closes; null during warm-up. */
function smaAtLast(bars: KLineData[], period: number): number | null {
  if (period < 1 || bars.length < period) return null
  let sum = 0
  for (let i = bars.length - period; i < bars.length; i++) sum += bars[i].close
  return sum / period
}

/** Standard EMA seeded with the SMA of the first `period` closes; null during warm-up. */
function emaAtLast(bars: KLineData[], period: number): number | null {
  if (period < 1 || bars.length < period) return null
  let ema = 0
  for (let i = 0; i < period; i++) ema += bars[i].close
  ema /= period
  const k = 2 / (period + 1)
  for (let i = period; i < bars.length; i++) {
    ema = ema + k * (bars[i].close - ema)
  }
  return ema
}

function maCandidates(
  bars: KLineData[],
  period: number,
  kind: 'sma' | 'ema',
): ReferenceCandidate[] {
  const value = kind === 'sma' ? smaAtLast(bars, period) : emaAtLast(bars, period)
  if (value === null || !isNum(value)) return []
  return [{ price: value, label: `${kind.toUpperCase()}(${period})` }]
}

/** Wilder ATR per bar (see seriesMath); undefined during warm-up. */
function atrSeries(bars: KLineData[], period: number): Num[] {
  if (period < 1) return bars.map(() => undefined)
  return wilderSeries(trueRangeSeries(bars), period)
}

/**
 * ATR stop per bar: close minus ATR for LONG, close plus ATR for SHORT, so
 * the level is adverse by construction. A stop driven to zero or below is
 * not a price, and reads as absent rather than a negative level.
 */
function atrStopSeries(bars: KLineData[], period: number, direction: SizingDirection): Num[] {
  const atr = atrSeries(bars, period)
  return bars.map((b, i) => {
    const a = atr[i]
    if (a == null) return undefined
    const stop = direction === 'long' ? b.close - a : b.close + a
    return stop > 0 ? stop : undefined
  })
}

function atrCandidates(
  bars: KLineData[],
  period: number,
  direction: SizingDirection,
): ReferenceCandidate[] {
  const series = atrStopSeries(bars, period, direction)
  const last = series[series.length - 1]
  if (last == null || !isNum(last)) return []
  return [{ price: last, label: `ATR(${period})` }]
}

function vwapCandidates(bars: KLineData[], tz: string): ReferenceCandidate[] {
  const series = computeVwap(bars, tz)
  const last = series[series.length - 1]
  // computeVwap already falls back to typical price on zero volume, so this
  // guard only catches a truly absent value.
  if (!last || !isNum(last.vwap)) return []
  return [{ price: last.vwap, label: 'VWAP' }]
}

function buildCandidates(inputs: SizingInputs): ReferenceCandidate[] {
  const tz = inputs.timezone || DEFAULT_SESSION_TZ
  const { bars } = inputs
  switch (inputs.source) {
    case 'manual':
      return manualCandidates(inputs.manualLevels, bars[bars.length - 1].timestamp)
    case 'pivot':
      return pivotCandidates(bars, tz)
    case 'pdh':
      return prevDayCandidates(bars, tz, 'high')
    case 'pdl':
      return prevDayCandidates(bars, tz, 'low')
    case 'sma':
      return maCandidates(bars, inputs.maPeriod, 'sma')
    case 'ema':
      return maCandidates(bars, inputs.maPeriod, 'ema')
    case 'vwap':
      return vwapCandidates(bars, tz)
    case 'atr':
      return atrCandidates(bars, inputs.maPeriod, inputs.direction)
  }
}

/**
 * The full readout: bind a reference via the relevance rule, then size.
 * Shares are FLOORED so realized risk never exceeds the input dollars; a
 * 0-share result is a legitimate readout (level too far for the budget), not
 * a dash.
 */
export function computeSizingReadout(inputs: SizingInputs): SizingReadout {
  const { bars, direction, riskDollars } = inputs
  const last = bars[bars.length - 1]
  if (!last || !isNum(riskDollars) || riskDollars <= 0) return { state: 'no-data' }
  const price = last.close
  if (!isNum(price)) return { state: 'no-data' }

  const selection = selectReference(direction, price, buildCandidates(inputs))
  if (selection.state !== 'level') return { state: selection.state }

  const distance = Math.abs(price - selection.price)
  const epsilon = Math.max(inputs.tick, price * 0.0005)
  if (distance < epsilon) return { state: 'too-close' }

  const sharesByRisk = Math.floor(riskDollars / distance)

  // The cap is a second, independent bound on size. Whichever binds first
  // wins, and `capped` records which one it was so the readout can say so:
  // a trader who asked to risk $500 and got a smaller size needs to know it
  // was the account ceiling talking, not a mis-drawn level.
  const cap = inputs.maxPositionDollars
  const capActive = isNum(cap) && cap > 0
  const sharesByCap = capActive ? Math.floor(cap / price) : Number.POSITIVE_INFINITY
  const shares = Math.min(sharesByRisk, sharesByCap)

  return {
    state: 'ok',
    label: selection.label,
    refPrice: selection.price,
    distance,
    shares,
    // Both derived from the FINAL share count, so a clamped size reports the
    // risk it actually carries rather than the risk that was requested.
    realized: shares * distance,
    price,
    positionValue: shares * price,
    capped: capActive && sharesByCap < sharesByRisk,
  }
}

/**
 * Per-bar stop levels for the drawn stop line. Series-shaped sources (sma,
 * ema, vwap, atr) track their curve bar by bar; scalar sources (manual,
 * pivot, pdh, pdl) fill a flat line at the bound level, and only once the
 * readout actually bound one. undefined = no line at that bar.
 */
export function computeStopSeries(inputs: SizingInputs, readout: SizingReadout): Num[] {
  const { bars } = inputs
  const tz = inputs.timezone || DEFAULT_SESSION_TZ
  switch (inputs.source) {
    case 'sma':
      if (inputs.maPeriod < 1) return bars.map(() => undefined)
      return rollingMean(
        bars.map((b) => b.close),
        inputs.maPeriod,
      )
    case 'ema':
      if (inputs.maPeriod < 1) return bars.map(() => undefined)
      return emaSeries(
        bars.map((b) => b.close),
        inputs.maPeriod,
      )
    case 'vwap':
      return computeVwap(bars, tz).map((v) => (isNum(v.vwap) ? v.vwap : undefined))
    case 'atr':
      return atrStopSeries(bars, inputs.maPeriod, inputs.direction)
    case 'manual':
    case 'pivot':
    case 'pdh':
    case 'pdl':
      return readout.state === 'ok' ? bars.map(() => readout.refPrice) : bars.map(() => undefined)
  }
}

/* ------------------------------------------------------------------ */
/*  Plan sizing — the two-click entry/stop drawing tool                 */
/* ------------------------------------------------------------------ */

export type PlanRiskType = 'dollars' | 'shares'

export interface PlanEntry {
  price: number
  /** Allocation as a percent of the INTENDED FULL position. A plan whose
   *  weights sum under 100 is deliberately partial: only that fraction of the
   *  risk budget is deployed (see `allocPct`). Relative ratios still set the
   *  blended entry; all-zero/garbage weights fall back to an equal split. */
  weightPct: number
}

export interface PlanSizingInputs {
  entries: PlanEntry[]
  stop: number
  riskType: PlanRiskType
  /** Dollars at risk when riskType is 'dollars'; total shares when 'shares'. */
  riskValue: number
  /** Ceiling on capital deployed (avg entry x shares). 0/undefined = no cap. */
  maxPositionDollars?: number
  /** Minimum price increment at the chart's precision (10^-precision). */
  tick: number
}

export type PlanSizing =
  | {
      state: 'ok'
      /** Inferred from geometry: stop below every entry = long, above = short. */
      direction: SizingDirection
      avgEntry: number
      sharesTotal: number
      /** Aligned with `entries`; floors of the weight split, remainder walked
       *  entry-by-entry, then trimmed so realized risk never exceeds the ask. */
      perEntryShares: number[]
      /** Realized: sum of shares_i x |entry_i - stop|. */
      riskDollars: number
      /** Capital deployed: sum of shares_i x entry_i. */
      positionValue: number
      capped: boolean
      /** Percent of the intended full position that is actually allocated
       *  (weight sum, snapped to 100 within int-rounding drift). Under 100
       *  means a deliberately partial plan — sizing above is scaled to it. */
      allocPct: number
    }
  | {
      state: 'invalid'
      reason: 'no-entries' | 'bad-input' | 'stop-through-entries' | 'too-close'
    }

/**
 * Size a drawn plan: one stop line, one or more entry lines with allocation
 * weights. Direction is never a setting here — the geometry says it. The
 * stop must sit strictly on ONE side of every entry (a stop between entries
 * has no coherent direction), and every entry must clear the epsilon guard.
 */
export function computePlanSizing(inputs: PlanSizingInputs): PlanSizing {
  const { entries, stop, riskType, riskValue } = inputs
  if (entries.length === 0) return { state: 'invalid', reason: 'no-entries' }
  if (!isNum(stop) || !isNum(riskValue) || riskValue <= 0 || entries.some((e) => !isNum(e.price))) {
    return { state: 'invalid', reason: 'bad-input' }
  }

  const allAbove = entries.every((e) => e.price > stop)
  const allBelow = entries.every((e) => e.price < stop)
  if (!allAbove && !allBelow) return { state: 'invalid', reason: 'stop-through-entries' }
  const direction: SizingDirection = allAbove ? 'long' : 'short'

  const epsilon = (price: number) => Math.max(inputs.tick, price * 0.0005)
  if (entries.some((e) => Math.abs(e.price - stop) < epsilon(e.price))) {
    return { state: 'invalid', reason: 'too-close' }
  }

  // Normalized weights; an all-zero (or negative-garbage) set means the user
  // never allocated, so split equally.
  const rawWeights = entries.map((e) => (isNum(e.weightPct) && e.weightPct > 0 ? e.weightPct : 0))
  const weightSum = rawWeights.reduce((a, b) => a + b, 0)
  const weights =
    weightSum > 0 ? rawWeights.map((w) => w / weightSum) : entries.map(() => 1 / entries.length)

  // Percent-of-intended-position semantics: a weight sum under 100 is a
  // deliberately partial plan, so only that fraction of the risk budget goes
  // on. Sums within integer-rounding drift of 100 snap to fully allocated
  // (equal-split seeds like [33,33,33] sum to 99 and must not read as 99%);
  // MAX_PLAN_ENTRIES is 5, so drift never exceeds 2.5. Sums over 100 clamp —
  // the cascade can't produce them, and over-deploying the budget is never ok.
  const allocPct = weightSum <= 0 || weightSum >= 97.5 ? 100 : Math.round(weightSum)
  const allocFraction = allocPct / 100

  const avgEntry = entries.reduce((acc, e, i) => acc + e.price * weights[i], 0)
  const avgDistance = Math.abs(avgEntry - stop)

  const riskBudget = riskValue * allocFraction
  let sharesTotal =
    riskType === 'shares' ? Math.floor(riskBudget) : Math.floor(riskBudget / avgDistance)

  const cap = inputs.maxPositionDollars
  const capActive = isNum(cap) && cap > 0
  const sharesByCap = capActive ? Math.floor(cap / avgEntry) : Number.POSITIVE_INFINITY
  const capped = capActive && sharesByCap < sharesTotal
  if (capped) sharesTotal = sharesByCap

  // Floor each entry's slice, then walk the remainder out one share at a time
  // in entry order so the total always lands exactly on sharesTotal.
  const perEntryShares = weights.map((w) => Math.floor(w * sharesTotal))
  let remainder = sharesTotal - perEntryShares.reduce((a, b) => a + b, 0)
  for (let i = 0; remainder > 0; i = (i + 1) % perEntryShares.length) {
    perEntryShares[i] += 1
    remainder -= 1
  }

  const realized = () =>
    perEntryShares.reduce((acc, s, i) => acc + s * Math.abs(entries[i].price - stop), 0)

  // The floor split shifts shares between entry prices, which can nudge the
  // realized risk past the requested dollars. Trim from the entry FARTHEST
  // from the stop (largest per-share risk) until the promise holds again.
  // Trims against the ALLOCATED budget, not the full ask — a partial plan's
  // realized risk must stay inside its fraction.
  if (riskType === 'dollars') {
    while (realized() > riskBudget && perEntryShares.some((s) => s > 0)) {
      let far = -1
      for (let i = 0; i < entries.length; i++) {
        if (perEntryShares[i] <= 0) continue
        if (far === -1 || Math.abs(entries[i].price - stop) > Math.abs(entries[far].price - stop)) {
          far = i
        }
      }
      perEntryShares[far] -= 1
    }
  }

  const finalTotal = perEntryShares.reduce((a, b) => a + b, 0)
  return {
    state: 'ok',
    direction,
    avgEntry,
    sharesTotal: finalTotal,
    perEntryShares,
    riskDollars: realized(),
    positionValue: perEntryShares.reduce((acc, s, i) => acc + s * entries[i].price, 0),
    capped,
    allocPct,
  }
}
